import { GameConfig } from "@/config/GameConfig";

export interface HowToPlayCard {
  title: string;
  /** Numeric `0xRRGGBB`, matching `GameConfig.colors` - converted to CSS by the Phaser rendering layer. */
  color: number;
  lines: string[];
  blurb: string;
}

function pct(fraction: number): number {
  return Math.round(fraction * 100);
}

/**
 * The single source of truth for "HOW TO PLAY" content, shared by the
 * main menu, the Pause menu, and the in-game help button so they can
 * never drift out of sync with each other. Every number here is derived
 * from `GameConfig` (never hand-typed), so a future balance change is
 * automatically reflected here too - this module is intentionally
 * Phaser-free so it can be unit tested directly.
 */
export function buildHowToPlayCards(): HowToPlayCard[] {
  const { safe, rush, chaos } = GameConfig.bites;
  const { starfruit, scoring } = GameConfig;
  const { maneuvers, diminishingReturns, flow, biteBonus, lastSecondTurn } = scoring;
  const occurrencePercents = diminishingReturns.map((factor) => pct(factor));
  const lastSecondTurnMin = Math.min(...lastSecondTurn.occurrenceCurve);
  const lastSecondTurnMax = Math.max(...lastSecondTurn.occurrenceCurve);

  return [
    {
      title: "CONTROLS",
      color: GameConfig.colors.accent,
      lines: ["Desktop: arrow keys to move. Space to pause/resume.", "Mobile: swipe in any direction to turn. Tap the pause button to pause/resume."],
      blurb: "That's it - everything else below is what you do with them.",
    },
    {
      title: "SAFE — RED APPLE",
      color: GameConfig.colors.biteSafe,
      lines: [
        `+${safe.growth} LENGTH • +${safe.score} SCORE`,
        "Ends the RUSH score combo, but never erases speed you've already built up - only a new run resets that.",
      ],
      blurb: "Choose SAFE when you need to regain control of your score combo, not your speed.",
    },
    {
      title: "RUSH — GOLD",
      color: GameConfig.colors.biteRush,
      lines: [
        `+${rush.growth} LENGTH`,
        `Score: ${rush.scoreByStack.join(" / ")}, capped at x${rush.maxStack}`,
        "Every RUSH also adds lasting speed pressure for the rest of the run - it never goes away on its own.",
        `Score stops climbing at x${rush.maxStack}, but speed pressure keeps building the more RUSH you eat.`,
        "SAFE or CHAOS ends the score combo - your accumulated speed stays exactly where it was.",
      ],
      blurb: "Finish faster and score more — the speed you build up is yours for the whole run.",
    },
    {
      title: "CHAOS — PURPLE",
      color: GameConfig.colors.biteChaos,
      lines: [
        "One bite. Four possible outcomes.",
        `${pct(chaos.outcomeWeightsCurrentV2.jackpot)}% JACKPOT — +${chaos.jackpot.growth} LENGTH, +${chaos.jackpot.score} SCORE`,
        `${pct(chaos.outcomeWeightsCurrentV2.wormhole)}% WORMHOLE — Cross any outer edge to wrap to the opposite side for ${chaos.wormhole.durationMs / 1000}s. Eating more fruit doesn't end it early. No score or length. Obstacles, walls and self-collision stay lethal.`,
        `${pct(chaos.outcomeWeightsCurrentV2.shrink)}% SHRINK — Lose up to ${chaos.shrink.segments} LENGTH and ${chaos.shrink.scoreLoss} SCORE`,
        `${pct(chaos.outcomeWeightsCurrentV2.speedTrial)}% SPEED TRIAL — Survive ${chaos.speedTrial.durationMs / 1000} seconds at a fixed ${GameConfig.speedEngine.speedTrialFixedStepMs}ms/step for +${chaos.speedTrial.rewardGrowth} LENGTH, +${chaos.speedTrial.rewardScore} SCORE.`,
        "CHAOS can take your score below zero.",
        "Every outcome ends the RUSH score combo, but never your accumulated speed.",
      ],
      blurb: "A real gamble: big reward, a wild edge-warp, speed challenge, or a painful penalty.",
    },
    {
      title: "STARFRUIT — LEGENDARY",
      color: GameConfig.colors.starfruitGlow,
      lines: [
        `${starfruit.probability * 100}% chance, rolled once per run — not per bite group.`,
        `Can appear at most once per run, only alongside the ${starfruit.revealGroupNumber === 2 ? "2nd" : `${starfruit.revealGroupNumber}th`} bite group onward.`,
        "Gives 0 score and 0 length - it's a temporary key, not a shortcut.",
        `Opens the gate for exactly ${starfruit.gateDurationMs / 1000} seconds. Reach it in time to exit; otherwise it closes and normal play continues.`,
      ],
      blurb: "Extremely rare. If you see it, the gate is the whole game.",
    },
    {
      title: "SKILL SCORE — MANEUVERS",
      color: GameConfig.colors.accent,
      lines: [
        `PERFECT FOLD +${maneuvers.perfectFold} — a tight U-turn: turn, then turn the same way again, at length 6+.`,
        `LAST-SECOND TURN +${lastSecondTurnMin} to +${lastSecondTurnMax} — turn away from a wall, obstacle, your own body, or BLACK HOLE you were one move from hitting. Worth more the faster you're moving, up to x${lastSecondTurn.speedMultiplier.max} at top speed.`,
        `TAIL GRAZE +${maneuvers.tailGraze} — pass within 1 cell of your own distant tail without touching it.`,
        `THREAD THE NEEDLE +${maneuvers.threadTheNeedle} — thread a one-cell-wide gap between hazards and come out the other side.`,
        `DOUBLE DANGER +${maneuvers.doubleDangerBonus} bonus — land 2+ different maneuvers on the exact same move.`,
        `Each maneuver type is worth less the more you repeat it: ${occurrencePercents.join("% → ")}% → 0%, forever after (LAST-SECOND TURN instead has its own longer, speed-scaled curve).`,
      ],
      blurb: "Rewards dangerous, precise movement - not just which fruit you pick.",
    },
    {
      title: "TRICK BITE & CLUTCH BITE",
      color: GameConfig.colors.star,
      lines: [
        `TRICK BITE +${biteBonus.trickBite} — eat any fruit within ${biteBonus.windowMoves} moves after a credited maneuver.`,
        `CLUTCH BITE +${biteBonus.clutchBite} — eat a fruit on the exact same move a maneuver credits. Replaces TRICK BITE for that fruit - they never stack.`,
        "Works for SAFE, RUSH, CHAOS, and STARFRUIT alike.",
        "Flat bonuses, not new maneuvers - never multiplied by FLOW or speed, never subject to diminishing returns.",
      ],
      blurb: "Turn a dangerous move into a rewarded bite.",
    },
    {
      title: "FLOW",
      color: GameConfig.colors.accentHover,
      lines: [
        `Chain credited maneuvers within ${flow.windowMoves} moves of each other to build FLOW.`,
        `Multiplier climbs with the streak: ${flow.multipliers.map((m) => `x${m.toFixed(2)}`).join(" → ")} (capped at x${flow.maxMultiplier.toFixed(2)}).`,
        "A duplicate/exhausted maneuver can't extend FLOW - it needs a real, freshly-credited one.",
      ],
      blurb: "Varied, skilled play compounds - looping one trick doesn't.",
    },
    {
      title: "PACE BONUS",
      color: GameConfig.colors.star,
      lines: [
        "Finish faster than the level's par time to earn bonus points - added once, only at completion.",
        "Pausing never costs you: only active play time counts against par.",
        "A STARFRUIT gate escape always earns 0 Pace Bonus - it isn't a real target-length finish.",
      ],
      blurb: "Efficient, decisive play is rewarded on top of everything else.",
    },
    {
      title: "CAMPAIGN STARS",
      color: GameConfig.colors.star,
      lines: [
        "Complete a level to earn at least 1 star, whatever your score.",
        "A higher Final Score earns 2 or 3 stars - RUSH streaks, skillful moves, WORMHOLE BITE, and Pace Bonus can all help get you there.",
        "Maps and worlds unlock by completing levels, never by star count.",
        "Stars and your best results only ever go up - a lower-scoring replay can never take one away.",
      ],
      blurb: "1 star means you finished it. 3 stars means you mastered it.",
    },
    {
      title: "BLACK HOLE",
      color: GameConfig.colors.blackHoleRing,
      lines: ["Instant death on contact - exactly like hitting a wall, an obstacle, or your own body.", "Not a portal or a gate - it never teleports or pulls you in. Just stay off that tile."],
      blurb: "See it, avoid it - there's no surviving a black hole.",
    },
  ];
}
